/**
 * Zod schemas — the validation contract shared by frontend and backend.
 *
 * Every API body is parsed through one of these before it touches a handler.
 * Types are derived from schemas (`z.infer`) where they must agree, but domain
 * interfaces in shared/types remain the canonical entity shape.
 *
 * Each domain has:
 *   - A full entity schema (for reading/parsing API responses)
 *   - An "input" schema (for create/update payloads, minus server-generated fields)
 */
import { z } from 'zod';
import {
  AUDIT_ACTION,
  CONFIDENTIAL_RANK,
  CONTACT_KIND,
  DOCUMENT_CATEGORY,
  EMPLOYMENT_STATUS,
  FEEDBACK_RELATIONSHIP,
  RECOGNITION_BADGE,
  REVIEW_CYCLE_STATUS,
  REVIEW_CYCLE_TYPE,
  REVIEW_STATUS,
  ROLE,
  SEVERITY,
  TIME_OFF_DURATION,
  TIME_OFF_STATUS,
  TIME_OFF_TYPE,
  TIME_OFF_TYPE_STORED,
  ONBOARDING_STATUS,
  OFFBOARDING_STATUS,
  VENDOR_CATEGORY,
  VENDOR_STATUS,
  WORK_LOCATION,
  I9_STATUS,
  ASSET_TYPE,
  ASSET_STATUS,
} from '../enums/index.js';

// ── Primitives ─────────────────────────────────────────────────────
export const ulidSchema = z.string().min(26).max(26);
export const isoDateSchema = z.string().datetime({ offset: true });
export const nullableString = z.string().nullable();

// ── Auth & identity ────────────────────────────────────────────────
export const roleSchema = z.enum(ROLE);

// ── Contacts ───────────────────────────────────────────────────────
export const contactKindSchema = z.enum(CONTACT_KIND);

export const contactEntrySchema = z.object({
  id: ulidSchema,
  kind: contactKindSchema,
  label: z.string().min(1).max(40),
  value: z.string().min(1).max(200),
  isPrimary: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
  personName: nullableString.optional(),
});

export const contactInputSchema = z.object({
  kind: contactKindSchema,
  label: z.string().min(1).max(40).default('Work'),
  value: z.string().min(1).max(200),
  isPrimary: z.boolean().default(false),
  personName: nullableString.optional().default(null),
});

/** Ensure email contacts have a primary; phone primary optional. */
function refineContacts(contacts: z.infer<typeof contactInputSchema>[], ctx: z.RefinementCtx) {
  const emails = contacts.filter((c) => c.kind === 'email');
  if (emails.length === 0) {
    ctx.addIssue({ code: 'custom', message: 'At least one email is required.', path: ['contacts'] });
    return;
  }
  for (const c of emails) {
    if (!z.string().email().safeParse(c.value).success) {
      ctx.addIssue({ code: 'custom', message: `Invalid email: ${c.value}`, path: ['contacts'] });
    }
  }
  const primaryEmails = emails.filter((c) => c.isPrimary);
  if (primaryEmails.length === 0) {
    emails[0]!.isPrimary = true;
  } else if (primaryEmails.length > 1) {
    ctx.addIssue({ code: 'custom', message: 'Only one primary email is allowed.', path: ['contacts'] });
  }
  const phones = contacts.filter((c) => c.kind === 'phone');
  const primaryPhones = phones.filter((c) => c.isPrimary);
  if (phones.length > 0 && primaryPhones.length === 0) {
    phones[0]!.isPrimary = true;
  } else if (primaryPhones.length > 1) {
    ctx.addIssue({ code: 'custom', message: 'Only one primary phone is allowed.', path: ['contacts'] });
  }
}

// ── Employee ───────────────────────────────────────────────────────
export const employmentStatusSchema = z.enum(EMPLOYMENT_STATUS);
export const workLocationSchema = z.enum(WORK_LOCATION);

export const employeeSchema = z.object({
  id: ulidSchema,
  userId: nullableString,
  name: z.string().min(1).max(200),
  legalName: nullableString,
  email: z.string().email(),
  department: z.string().min(1).max(100),
  title: z.string().min(1).max(100),
  hireDate: isoDateSchema,
  managerId: nullableString,
  employmentStatus: employmentStatusSchema,
  avatarStorageKey: nullableString,
  phone: nullableString,
  employeeNumber: nullableString,
  workLocation: workLocationSchema,
  contacts: z.array(contactEntrySchema).default([]),
});

const employeeProfileBase = z.object({
  name: z.string().min(1).max(200),
  /** Preferred / known-as name is `name`. Optional formal legal name. */
  legalName: z.string().min(1).max(200).nullable().optional().default(null),
  department: z.string().min(1).max(100),
  title: z.string().min(1).max(100),
  hireDate: isoDateSchema,
  managerId: nullableString.default(null),
  employmentStatus: employmentStatusSchema.default('Active'),
  employeeNumber: z.string().min(1).max(40).nullable().optional().default(null),
  workLocation: workLocationSchema.default('OnSite'),
  /** Optional Key Fob serial when creating an OnSite employee. */
  keyFobSerial: z.string().min(1).max(64).optional(),
  /** Preferred: full contact list. */
  contacts: z.array(contactInputSchema).optional(),
  /** Legacy single fields — used when `contacts` is omitted. */
  email: z.string().email().optional(),
  phone: nullableString.optional().default(null),
});

function resolveEmployeeContacts(
  data: z.infer<typeof employeeProfileBase>,
  ctx: z.RefinementCtx,
): z.infer<typeof contactInputSchema>[] {
  let contacts = data.contacts ? [...data.contacts] : [];
  if (contacts.length === 0) {
    if (!data.email) {
      ctx.addIssue({ code: 'custom', message: 'Email is required.', path: ['email'] });
      return [];
    }
    contacts = [
      { kind: 'email' as const, label: 'Work', value: data.email, isPrimary: true, personName: null },
    ];
    if (data.phone) {
      contacts.push({
        kind: 'phone' as const,
        label: 'Work',
        value: data.phone,
        isPrimary: true,
        personName: null,
      });
    }
  }
  refineContacts(contacts, ctx);
  return contacts;
}

export const employeeCreateInput = employeeProfileBase
  .extend({
    role: roleSchema.default('Employee'),
    password: z.string().min(8),
  })
  .superRefine((data, ctx) => {
    resolveEmployeeContacts(data, ctx);
  })
  .transform((data) => {
    const contacts = data.contacts?.length
      ? data.contacts
      : [
          {
            kind: 'email' as const,
            label: 'Work',
            value: data.email!,
            isPrimary: true,
            personName: null,
          },
          ...(data.phone
            ? [{
                kind: 'phone' as const,
                label: 'Work',
                value: data.phone,
                isPrimary: true,
                personName: null,
              }]
            : []),
        ];
    const emails = contacts.filter((c) => c.kind === 'email');
    if (emails.length && !emails.some((c) => c.isPrimary)) emails[0]!.isPrimary = true;
    const phones = contacts.filter((c) => c.kind === 'phone');
    if (phones.length && !phones.some((c) => c.isPrimary)) phones[0]!.isPrimary = true;
    const primaryEmail = emails.find((c) => c.isPrimary)?.value ?? emails[0]!.value;
    const primaryPhone = phones.find((c) => c.isPrimary)?.value ?? phones[0]?.value ?? null;
    return {
      name: data.name,
      legalName: data.legalName ?? null,
      department: data.department,
      title: data.title,
      hireDate: data.hireDate,
      managerId: data.managerId,
      employmentStatus: data.employmentStatus,
      employeeNumber: data.employeeNumber ?? null,
      workLocation: data.workLocation,
      keyFobSerial: data.keyFobSerial,
      contacts,
      email: primaryEmail,
      phone: primaryPhone,
      role: data.role,
      password: data.password,
    };
  });

// Profile fields only — role/password managed via /api/users on the employee detail page.
export const employeeUpdateInput = employeeProfileBase
  .omit({ keyFobSerial: true })
  .partial()
  .superRefine((data, ctx) => {
    if (data.contacts !== undefined || data.email !== undefined || data.phone !== undefined) {
      // Build a temporary object for resolution when any contact field is present.
      if (data.contacts?.length || data.email) {
        resolveEmployeeContacts(
          {
            name: data.name ?? 'x',
            legalName: data.legalName ?? null,
            department: data.department ?? 'x',
            title: data.title ?? 'x',
            hireDate: data.hireDate ?? new Date().toISOString(),
            managerId: data.managerId ?? null,
            employmentStatus: data.employmentStatus ?? 'Active',
            employeeNumber: data.employeeNumber ?? null,
            workLocation: data.workLocation ?? 'OnSite',
            contacts: data.contacts,
            email: data.email,
            phone: data.phone ?? null,
          },
          ctx,
        );
      }
    }
  })
  .transform((data) => {
    const base: {
      name?: string;
      legalName?: string | null;
      department?: string;
      title?: string;
      hireDate?: string;
      managerId?: string | null;
      employmentStatus?: 'Active' | 'OnLeave' | 'Terminated';
      employeeNumber?: string | null;
      workLocation?: 'OnSite' | 'Remote';
      email?: string;
      phone?: string | null;
      contacts?: Array<{
        kind: 'email' | 'phone';
        label: string;
        value: string;
        isPrimary: boolean;
        personName: string | null;
      }>;
    } = {
      name: data.name,
      legalName: data.legalName,
      department: data.department,
      title: data.title,
      hireDate: data.hireDate,
      managerId: data.managerId,
      employmentStatus: data.employmentStatus,
      employeeNumber: data.employeeNumber,
      workLocation: data.workLocation,
    };
    if (data.contacts === undefined && data.email === undefined && data.phone === undefined) {
      return base;
    }
    const contacts = data.contacts?.length
      ? data.contacts
      : data.email
        ? [
            {
              kind: 'email' as const,
              label: 'Work',
              value: data.email,
              isPrimary: true,
              personName: null,
            },
            ...(data.phone
              ? [{
                  kind: 'phone' as const,
                  label: 'Work',
                  value: data.phone,
                  isPrimary: true,
                  personName: null,
                }]
              : []),
          ]
        : undefined;
    if (!contacts) {
      return {
        ...base,
        phone: data.phone,
      };
    }
    const emails = contacts.filter((c) => c.kind === 'email');
    if (emails.length && !emails.some((c) => c.isPrimary)) emails[0]!.isPrimary = true;
    const phones = contacts.filter((c) => c.kind === 'phone');
    if (phones.length && !phones.some((c) => c.isPrimary)) phones[0]!.isPrimary = true;
    const primaryEmail = emails.find((c) => c.isPrimary)?.value ?? emails[0]?.value;
    const primaryPhone = phones.find((c) => c.isPrimary)?.value ?? phones[0]?.value ?? null;
    return {
      ...base,
      contacts,
      email: primaryEmail,
      phone: primaryPhone,
    };
  });

// ── NAID ───────────────────────────────────────────────────────────
export const i9StatusSchema = z.enum(I9_STATUS);

export const naidUpdateInput = z.object({
  drugScreeningOn: z.string().nullable().optional(),
  backgroundOn: z.string().nullable().optional(),
  yearlyTestOn: z.string().nullable().optional(),
  ndaYear: z.number().int().min(1990).max(2100).nullable().optional(),
  i9Status: i9StatusSchema.optional(),
  yearlyTestResult: z.string().max(200).nullable().optional(),
  accessRequired: z.boolean().optional(),
  incompleteNotificationsExempt: z.boolean().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

// ── Company assets ─────────────────────────────────────────────────
export const assetTypeSchema = z.enum(ASSET_TYPE);
export const assetStatusSchema = z.enum(ASSET_STATUS);

export const assetAssignInput = z.object({
  employeeId: ulidSchema,
  assetType: assetTypeSchema.default('KeyFob'),
  serialNumber: z.string().min(1).max(64),
  assignedOn: z.string().optional(),
  notes: z.string().max(1000).nullable().optional(),
});

export const assetReturnInput = z.object({
  returnedOn: z.string().optional(),
  notes: z.string().max(1000).nullable().optional(),
});

// ── Vendors ────────────────────────────────────────────────────────
export const vendorStatusSchema = z.enum(VENDOR_STATUS);
export const vendorCategorySchema = z.enum(VENDOR_CATEGORY);

export const vendorSchema = z.object({
  id: ulidSchema,
  name: z.string().min(1).max(200),
  category: z.string().min(1).max(100),
  status: vendorStatusSchema,
  website: nullableString,
  notes: nullableString,
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
  contacts: z.array(contactEntrySchema).default([]),
});

export const vendorCreateInput = z.object({
  name: z.string().min(1).max(200),
  category: z.string().min(1).max(100).default('Other'),
  status: vendorStatusSchema.default('Active'),
  website: nullableString.default(null),
  notes: nullableString.default(null),
  contacts: z.array(contactInputSchema).default([]),
});

export const vendorUpdateInput = vendorCreateInput.partial();

// ── Reviews ────────────────────────────────────────────────────────
export const reviewCycleTypeSchema = z.enum(REVIEW_CYCLE_TYPE);
export const reviewCycleStatusSchema = z.enum(REVIEW_CYCLE_STATUS);
export const reviewStatusSchema = z.enum(REVIEW_STATUS);
export const feedbackRelationshipSchema = z.enum(FEEDBACK_RELATIONSHIP);

export const reviewRatingsSchema = z.object({
  performance: z.number().min(1).max(5),
  culture: z.number().min(1).max(5),
  communication: z.number().min(1).max(5),
  dependability: z.number().min(1).max(5),
});

/** Airtable-style Weekly 1:1 KPI scorecard stored in reviews.ratings JSON. */
export const weeklyScorecardSchema = z.object({
  kind: z.literal('weekly'),
  periodStart: z.string().min(10),
  periodEnd: z.string().min(10),
  totalDials: z.number().min(0).max(100000),
  liveConnects: z.number().min(0).max(100000),
  qualifiedConversations: z.number().min(0).max(100000),
  newSupportersAdded: z.number().min(0).max(100000),
  devicesReceived: z.number().min(0).max(100000),
  closeRate: z.number().min(0).max(100).nullable(),
  avgDevicesPerSupporter: z.number().min(0).max(100000),
  crmCompliance: z.number().min(0).max(100),
  overallPerformanceScore: z.number().min(0).max(100),
  wins: z.string().max(5000).default(''),
  blockers: z.string().max(5000).default(''),
  actions: z.string().max(5000).default(''),
});

export const anyReviewRatingsSchema = z.union([weeklyScorecardSchema, reviewRatingsSchema]);

export const reviewCycleSchema = z.object({
  id: ulidSchema,
  name: z.string().min(1).max(200),
  type: reviewCycleTypeSchema,
  opensAt: isoDateSchema,
  closesAt: isoDateSchema,
  status: reviewCycleStatusSchema,
});

export const reviewCycleCreateInput = z.object({
  name: z.string().min(1).max(200),
  type: reviewCycleTypeSchema,
  opensAt: isoDateSchema,
  closesAt: isoDateSchema,
  status: reviewCycleStatusSchema.default('Open'),
});

export const reviewSchema = z.object({
  id: ulidSchema,
  cycleId: ulidSchema,
  employeeId: ulidSchema,
  reviewerId: ulidSchema,
  type: reviewCycleTypeSchema,
  ratings: anyReviewRatingsSchema,
  qualitativeFeedback: z.string(),
  goalsForNextPeriod: z.string(),
  status: reviewStatusSchema,
  visibleToEmployeeAt: nullableString,
});

export const reviewCreateInput = z.object({
  cycleId: ulidSchema,
  employeeId: ulidSchema,
  type: reviewCycleTypeSchema,
  ratings: anyReviewRatingsSchema,
  qualitativeFeedback: z.string().default(''),
  goalsForNextPeriod: z.string().default(''),
  status: reviewStatusSchema.default('Draft'),
});

export const reviewUpdateInput = z.object({
  ratings: anyReviewRatingsSchema.optional(),
  qualitativeFeedback: z.string().optional(),
  goalsForNextPeriod: z.string().optional(),
});

export const reviewFeedbackSchema = z.object({
  id: ulidSchema,
  reviewId: ulidSchema,
  authorId: ulidSchema,
  relationship: feedbackRelationshipSchema,
  ratings: reviewRatingsSchema.nullable(),
  comments: z.string(),
  submittedAt: isoDateSchema,
});

export const reviewFeedbackCreateInput = z.object({
  relationship: feedbackRelationshipSchema,
  ratings: reviewRatingsSchema.nullable().default(null),
  comments: z.string().min(1),
});

// ── Recognition ────────────────────────────────────────────────────
export const recognitionBadgeSchema = z.enum(RECOGNITION_BADGE);

export const recognitionSchema = z.object({
  id: ulidSchema,
  employeeId: ulidSchema,
  givenBy: ulidSchema,
  badge: recognitionBadgeSchema,
  text: z.string().min(1).max(1000),
  date: isoDateSchema,
  public: z.boolean(),
});

export const recognitionCreateInput = z.object({
  employeeId: ulidSchema,
  badge: recognitionBadgeSchema,
  text: z.string().min(1).max(1000),
  date: isoDateSchema,
  public: z.boolean().default(true),
});

export const recognitionUpdateInput = z.object({
  badge: recognitionBadgeSchema.optional(),
  text: z.string().min(1).max(1000).optional(),
  date: isoDateSchema.optional(),
  public: z.boolean().optional(),
});

// ── Disciplinary ───────────────────────────────────────────────────
export const severitySchema = z.enum(SEVERITY);

export const disciplinaryActionSchema = z.object({
  id: ulidSchema,
  employeeId: ulidSchema,
  date: isoDateSchema,
  severity: severitySchema,
  details: z.string().min(1),
  actionPlan: z.string(),
  dueDate: nullableString,
  issuedById: ulidSchema,
  acknowledgedAt: nullableString,
});

export const disciplinaryCreateInput = z.object({
  employeeId: ulidSchema,
  date: isoDateSchema,
  severity: severitySchema,
  details: z.string().min(1),
  actionPlan: z.string().default(''),
  dueDate: nullableString.default(null),
});

export const disciplinaryUpdateInput = z.object({
  date: isoDateSchema.optional(),
  severity: severitySchema.optional(),
  details: z.string().min(1).optional(),
  actionPlan: z.string().optional(),
  dueDate: nullableString.optional(),
});

// ── Documented conversations ───────────────────────────────────────
export const conversationSchema = z.object({
  id: ulidSchema,
  employeeId: ulidSchema,
  date: isoDateSchema,
  topic: z.string().min(1),
  details: z.string(),
  witnesses: z.string(),
  authorId: ulidSchema,
});

export const conversationCreateInput = z.object({
  employeeId: ulidSchema,
  date: isoDateSchema,
  topic: z.string().min(1),
  details: z.string().min(1),
  witnesses: z.string().default(''),
});

export const conversationUpdateInput = z.object({
  date: isoDateSchema.optional(),
  topic: z.string().min(1).optional(),
  details: z.string().min(1).optional(),
  witnesses: z.string().optional(),
});

// ── Employee file notes (admin-only) ───────────────────────────────
export const fileNoteAttachmentSchema = z.object({
  id: ulidSchema,
  noteId: ulidSchema,
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  uploadedBy: ulidSchema,
  uploadedAt: isoDateSchema,
});

export const fileNoteSchema = z.object({
  id: ulidSchema,
  employeeIds: z.array(ulidSchema).min(1),
  date: isoDateSchema,
  title: z.string().min(1),
  details: z.string(),
  authorId: ulidSchema,
  attachments: z.array(fileNoteAttachmentSchema),
});

export const fileNoteCreateInput = z.object({
  employeeIds: z.array(ulidSchema).min(1),
  date: isoDateSchema,
  title: z.string().min(1),
  details: z.string().min(1),
});

export const fileNoteUpdateInput = z.object({
  employeeIds: z.array(ulidSchema).min(1).optional(),
  date: isoDateSchema.optional(),
  title: z.string().min(1).optional(),
  details: z.string().min(1).optional(),
});

// ── Time off ───────────────────────────────────────────────────────
export const timeOffTypeSchema = z.enum(TIME_OFF_TYPE);
export const timeOffTypeStoredSchema = z.enum(TIME_OFF_TYPE_STORED);
export const timeOffDurationSchema = z.enum(TIME_OFF_DURATION);
export const timeOffStatusSchema = z.enum(TIME_OFF_STATUS);
const timeOffTimeSchema = z.string().regex(/^\d{2}:\d{2}$/);

export const timeOffRequestSchema = z.object({
  id: ulidSchema,
  employeeId: ulidSchema,
  type: timeOffTypeStoredSchema,
  from: isoDateSchema,
  to: isoDateSchema,
  duration: timeOffDurationSchema,
  startTime: timeOffTimeSchema.nullable(),
  endTime: timeOffTimeSchema.nullable(),
  reason: z.string().max(1000).nullable(),
  status: timeOffStatusSchema,
  decidedBy: nullableString,
  decidedAt: nullableString,
});

export const timeOffCreateInput = z.object({
  employeeId: ulidSchema,
  type: timeOffTypeSchema,
  from: isoDateSchema,
  to: isoDateSchema,
  duration: timeOffDurationSchema.default('FullDay'),
  startTime: timeOffTimeSchema.nullable().optional().default(null),
  endTime: timeOffTimeSchema.nullable().optional().default(null),
  reason: z.string().trim().max(1000).optional(),
}).superRefine(({ duration, from, to, startTime, endTime }, ctx) => {
  if (duration === 'FullDay' && (startTime || endTime)) {
    ctx.addIssue({ code: 'custom', message: 'Full-day requests cannot include times.', path: ['duration'] });
  }
  if (duration === 'PartialDay') {
    if (from.slice(0, 10) !== to.slice(0, 10)) {
      ctx.addIssue({ code: 'custom', message: 'Partial-day requests must be on one date.', path: ['to'] });
    }
    if (!startTime || !endTime || startTime >= endTime) {
      ctx.addIssue({ code: 'custom', message: 'Choose an end time after the start time.', path: ['endTime'] });
    }
  }
});

export const timeOffDecideInput = z.object({
  status: z.enum(['Approved', 'Denied']),
});

export const timeOffUpdateInput = z.object({
  type: timeOffTypeSchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  duration: timeOffDurationSchema.optional(),
  startTime: timeOffTimeSchema.nullable().optional(),
  endTime: timeOffTimeSchema.nullable().optional(),
  reason: z.string().trim().max(1000).optional(),
});

export const ptoBalanceSchema = z.object({
  employeeId: ulidSchema,
  year: z.number().int().min(2000).max(2100),
  allotmentDays: z.number().min(0).max(366),
  usedDays: z.number().min(0),
  pendingDays: z.number().min(0),
  remainingDays: z.number(),
  notes: z.string().max(500).nullable(),
  updatedAt: isoDateSchema.nullable(),
});

export const ptoBalanceUpsertInput = z.object({
  employeeId: ulidSchema,
  year: z.number().int().min(2000).max(2100),
  allotmentDays: z.number().min(0).max(366),
  notes: z.string().max(500).nullable().optional(),
});

// ── Onboarding ─────────────────────────────────────────────────────
export const onboardingStatusSchema = z.enum(ONBOARDING_STATUS);

export const onboardingTemplateItemInput = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional().default(''),
  sortOrder: z.number().int().min(0).max(999).optional(),
  required: z.boolean().optional().default(true),
});

export const onboardingTemplateCreateInput = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional().default(''),
  isActive: z.boolean().optional().default(true),
  items: z.array(onboardingTemplateItemInput).min(1).max(50),
});

export const onboardingTemplateUpdateInput = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).optional(),
  isActive: z.boolean().optional(),
  items: z.array(onboardingTemplateItemInput).min(1).max(50).optional(),
});

export const onboardingAssignInput = z.object({
  templateId: ulidSchema,
  employeeId: ulidSchema,
  dueDate: isoDateSchema.nullable().optional(),
});

export const onboardingAssignmentSchema = z.object({
  id: ulidSchema,
  templateId: ulidSchema,
  templateName: z.string(),
  employeeId: ulidSchema,
  assignedBy: ulidSchema,
  status: onboardingStatusSchema,
  dueDate: isoDateSchema.nullable(),
  assignedAt: isoDateSchema,
  completedAt: isoDateSchema.nullable(),
  completedCount: z.number().int().optional(),
  totalCount: z.number().int().optional(),
  requiredRemaining: z.number().int().optional(),
});

// ── Offboarding ────────────────────────────────────────────────────
export const offboardingStatusSchema = z.enum(OFFBOARDING_STATUS);

export const offboardingTemplateItemInput = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional().default(''),
  sortOrder: z.number().int().min(0).max(999).optional(),
  required: z.boolean().optional().default(true),
});

export const offboardingTemplateCreateInput = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional().default(''),
  isActive: z.boolean().optional().default(true),
  items: z.array(offboardingTemplateItemInput).min(1).max(50),
});

export const offboardingTemplateUpdateInput = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).optional(),
  isActive: z.boolean().optional(),
  items: z.array(offboardingTemplateItemInput).min(1).max(50).optional(),
});

export const offboardingAssignInput = z.object({
  templateId: ulidSchema,
  employeeId: ulidSchema,
  dueDate: isoDateSchema.nullable().optional(),
});

export const offboardingAssignmentSchema = z.object({
  id: ulidSchema,
  templateId: ulidSchema,
  templateName: z.string(),
  employeeId: ulidSchema,
  assignedBy: ulidSchema,
  status: offboardingStatusSchema,
  dueDate: isoDateSchema.nullable(),
  assignedAt: isoDateSchema,
  completedAt: isoDateSchema.nullable(),
  completedCount: z.number().int().optional(),
  totalCount: z.number().int().optional(),
  requiredRemaining: z.number().int().optional(),
});


// ── Documents ──────────────────────────────────────────────────────
export const documentCategorySchema = z.enum(DOCUMENT_CATEGORY);
export const confidentialRankSchema = z.enum(CONFIDENTIAL_RANK);

export const documentSchema = z.object({
  id: ulidSchema,
  employeeId: ulidSchema,
  name: z.string().min(1),
  storageKey: z.string().min(1),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedBy: ulidSchema,
  uploadedAt: isoDateSchema,
  category: documentCategorySchema,
  confidentialRank: confidentialRankSchema,
});

export const documentCreateInput = z.object({
  employeeId: ulidSchema,
  name: z.string().min(1),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  category: documentCategorySchema,
  confidentialRank: confidentialRankSchema,
});

export const documentUpdateInput = z.object({
  name: z.string().min(1).optional(),
  category: documentCategorySchema.optional(),
  confidentialRank: confidentialRankSchema.optional(),
});

// ── Audit ──────────────────────────────────────────────────────────
export const auditActionSchema = z.enum(AUDIT_ACTION);

export const auditEntrySchema = z.object({
  id: ulidSchema,
  actorUserId: ulidSchema,
  action: auditActionSchema,
  entityType: z.string(),
  entityId: nullableString,
  before: z.unknown(),
  after: z.unknown(),
  at: isoDateSchema,
  ip: nullableString,
  userAgent: nullableString,
});

// ── API helpers ────────────────────────────────────────────────────
export const paginatedSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });

export const apiErrorSchema = z.object({
  code: z.enum([
    'NOT_FOUND',
    'FORBIDDEN',
    'VALIDATION',
    'CONFLICT',
    'INTERNAL',
    'UNAUTHORIZED',
  ]),
  message: z.string(),
  fields: z.record(z.string(), z.string()).optional(),
});

export const listQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(25),
  search: z.string().optional(),
});

// ── Auth ───────────────────────────────────────────────────────────
export const userSchema = z.object({
  id: ulidSchema,
  email: z.string().email(),
  name: z.string(),
  pictureUrl: nullableString,
  role: roleSchema,
  employeeId: nullableString,
  createdAt: isoDateSchema,
});

export const loginInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

export const changePasswordInputSchema = z.object({
  currentPassword: z.string().min(8),
  newPassword: z.string().min(8),
});

export const userCreateInputSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  password: z.string().min(8),
  role: roleSchema,
  employeeId: nullableString.default(null),
});

export const adminResetPasswordSchema = z.object({
  newPassword: z.string().min(8),
});

export const adminUpdateRoleSchema = z.object({
  role: roleSchema,
});

export const userUpdateInputSchema = z.object({
  name: z.string().min(1).optional(),
  email: z.string().email().optional(),
  employeeId: nullableString.optional(),
});


// ── Notifications ─────────────────────────────────────────────────
export const notificationSchema = z.object({
  id: ulidSchema,
  userId: ulidSchema,
  type: z.string().min(1).max(80),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(2000),
  href: z.string().max(500).nullable(),
  entityType: z.string().max(80).nullable(),
  entityId: z.string().max(40).nullable(),
  readAt: isoDateSchema.nullable(),
  createdAt: isoDateSchema,
});

// ── Company branding ───────────────────────────────────────────────
export const companyBrandingUpdateInput = z.object({
  companyName: z.string().trim().min(1).max(120).optional(),
  addressLines: z.array(z.string().trim().max(200)).max(8).optional(),
});

// ── Company announcements ─────────────────────────────────────────
export const companyAnnouncementSchema = z.object({
  id: ulidSchema,
  title: z.string().min(1).max(160),
  body: z.string().min(1).max(2000),
  createdBy: ulidSchema,
  createdByName: z.string().min(1).max(160),
  createdAt: isoDateSchema,
});

export const companyAnnouncementCreateInput = z.object({
  title: z.string().trim().min(1).max(160),
  body: z.string().trim().min(1).max(2000),
});

